/**
 * Extracts files from a drop, descending into any dropped folders.
 *
 * Lab scans usually live one folder per roll, so dropping the folder onto its
 * card is the gesture admins reach for first. `dataTransfer.files` is empty for
 * a directory, so folders have to be walked via the (non-standard but
 * universally supported) `webkitGetAsEntry` filesystem API.
 */

/**
 * Guards against pathological nesting. Real scan folders are one or two levels
 * deep; anything past this is skipped rather than risking an unbounded walk.
 */
const MAX_DEPTH = 16;

/**
 * Pulls files out of a drop, expanding directories.
 *
 * The synchronous prologue matters: a `DataTransferItemList` is neutered the
 * moment the drop handler returns, so every entry must be captured *before* the
 * first await. Only the traversal itself is async.
 */
export function readDroppedFiles(dataTransfer: DataTransfer): Promise<File[]> {
  const entries: FileSystemEntry[] = [];
  const looseFiles: File[] = [];

  // --- synchronous capture ---
  const items = dataTransfer.items;
  if (items) {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind !== 'file') continue;
      const entry = item.webkitGetAsEntry?.() ?? null;
      if (entry) {
        entries.push(entry);
      } else {
        // No filesystem entry available — fall back to the plain File.
        const file = item.getAsFile();
        if (file) looseFiles.push(file);
      }
    }
  }

  if (entries.length === 0) {
    const fallback = looseFiles.length > 0 ? looseFiles : Array.from(dataTransfer.files ?? []);
    return Promise.resolve(fallback);
  }

  // --- async traversal ---
  return Promise.all(entries.map((entry) => collectEntry(entry, 0)))
    .then((collected) => [...looseFiles, ...collected.flat()])
    .then(sortNaturally);
}

/** Recursively collects the files under one entry. */
async function collectEntry(entry: FileSystemEntry, depth: number): Promise<File[]> {
  // Skip dotfiles: a macOS folder carries .DS_Store and friends, which would
  // otherwise surface as noise in the "files skipped" notice.
  if (entry.name.startsWith('.')) return [];

  if (entry.isFile) {
    const file = await readFile(entry as FileSystemFileEntry);
    return file ? [file] : [];
  }

  if (entry.isDirectory && depth < MAX_DEPTH) {
    const children = await readDirectory(entry as FileSystemDirectoryEntry);
    const nested = await Promise.all(children.map((child) => collectEntry(child, depth + 1)));
    return nested.flat();
  }

  return [];
}

/** Resolves to null rather than rejecting — one unreadable file shouldn't sink the drop. */
function readFile(entry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => {
    entry.file(
      (file) => resolve(file),
      () => resolve(null)
    );
  });
}

/**
 * Reads a directory to exhaustion.
 *
 * `readEntries` returns a *batch* (Chrome caps it at 100), not the whole
 * listing, and must be called repeatedly until it yields an empty array —
 * a single call silently truncates any roll of more than 100 frames.
 */
function readDirectory(entry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  return new Promise((resolve) => {
    const reader = entry.createReader();
    const all: FileSystemEntry[] = [];

    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) {
            resolve(all);
            return;
          }
          all.push(...batch);
          readBatch();
        },
        () => resolve(all)
      );
    };

    readBatch();
  });
}

/**
 * Directory listings arrive in no guaranteed order, so sort by filename the way
 * the backend does — numerically aware, so frame 2 precedes frame 10.
 */
function sortNaturally(files: File[]): File[] {
  return [...files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
  );
}
